import React, { forwardRef, memo } from 'react'
import styled from '@emotion/styled'
import { css, SerializedStyles } from '@emotion/react'

import InputBase from '@src/components/inputs/InputBase'

type InputProps = {
	label?: string
	isError?: boolean
	disabled?: boolean
	errorMsg?: string
	wrapperStyle?: SerializedStyles
	labelStyle?: SerializedStyles
	errorStyle?: SerializedStyles
} & React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>

const Input = (
	{ label, isError, disabled, errorMsg, wrapperStyle, labelStyle, errorStyle, ...rest }: InputProps,
	ref: React.Ref<HTMLInputElement>,
) => {
	return (
		<InputBase css={[wrapperBasicStyle, wrapperStyle]}>
			{label !== undefined && (
				<InputLabel htmlFor={rest.id} css={labelStyle}>
					{label}
				</InputLabel>
			)}
			<div>
				<InputStyle
					ref={ref}
					isError={isError}
					disabled={disabled}
					onWheel={(e) => {
						// Disable scroll inc/dec for number val
						if (e.currentTarget.type === 'number' && e?.currentTarget?.blur) {
							e.currentTarget.blur()
						}
					}}
					{...rest}
				/>
				{errorMsg && <ErrorMsg css={errorStyle}>{errorMsg}</ErrorMsg>}
			</div>
		</InputBase>
	)
}

const wrapperBasicStyle = css`
	display: flex;
	flex-direction: row;
	width: 100%;
	padding: 1rem 0;
`

const InputLabel = styled.label`
	flex: 0 0 auto;
	display: inline-flex;
	align-items: center;
	font-size: 1.3rem;
	font-weight: 600;
	line-height: 2.2rem;
	width: 13rem;
	height: 3.4rem;
`

const InputStyle = styled.input<InputProps>`
	border: 1px solid #92959e;
	color: #121212;
	outline: none;
	padding: 0.5rem 1.1rem;
	border-radius: 0.5rem;
	background: #fff;

	&:focus {
		border: 1px solid #000;
	}
	${({ disabled }) =>
		disabled &&
		css`
			background: #f3f3f3;
		`}
	${({ isError }) =>
		isError &&
		css`
			border: 1px solid #de0000;
			&:focus {
				border: 1px solid #de0000;
			}
		`}

	&::-webkit-outer-spin-button,
	&::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
`

const ErrorMsg = styled.div`
	padding-top: 0.4rem;
	color: #de0000;
	font-size: 1.2rem;
	line-height: 2rem;
	font-weight: 600;
`

export default memo(forwardRef<HTMLInputElement, InputProps>(Input))
